/**
 * AuditManagementPage
 * 
 * Página para gerenciar auditorias concluídas
 * Exibe diferenças e status dos itens auditados
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown,
  Loader2, X, Package, User, Edit2, ChevronDown, Filter, RotateCcw
} from 'lucide-react'
import Avatar from '../../../../components/Avatar/Avatar'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import { stockAuditService } from '../../../../services/erpApi'
import { usePermissions } from '../../../../hooks/usePermissions'
import { MODULES, ACTIONS } from '../../../../utils/permissions'
import './AuditManagementPage.css'

interface AuditItem {
  id: number
  product_id: number
  product_sku?: string
  product_name?: string
  product_barcode?: string
  location_code?: string
  expected_quantity: number
  expected_quantity_at_count?: number
  effective_expected_quantity?: number
  counted_quantity?: number
  difference?: number
  status: 'pending' | 'counted' | 'divergent' | 'matched'
  notes?: string
}

interface AuditData {
  id: number
  audit_number: string
  brand_id: number
  brand_name: string
  status: 'pending' | 'in_progress' | 'completed' | 'applied' | 'cancelled'
  start_date: string
  created_by_name?: string
  responsible_user_id?: number
  responsible_user_name?: string
  items: AuditItem[]
}

interface EligibleUser {
  id: number
  name: string
  email: string
  avatar_url?: string
  group_name?: string
}

function AuditManagementPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [updatingResponsible, setUpdatingResponsible] = useState(false)
  const [auditData, setAuditData] = useState<AuditData | null>(null)
  const [items, setItems] = useState<AuditItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
  const [eligibleUsers, setEligibleUsers] = useState<EligibleUser[]>([])
  const [showResponsibleEdit, setShowResponsibleEdit] = useState(false)
  const [selectedResponsibleId, setSelectedResponsibleId] = useState<number | null>(null)
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [showReopenModal, setShowReopenModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)

  const canEditResponsible = hasPermission(MODULES.STOCK, ACTIONS.STOCK_AUDITS_EDIT_RESPONSIBLE)
  const canReopen = hasPermission(MODULES.STOCK, ACTIONS.STOCK_AUDIT_REOPEN)

  const loadEligibleUsers = useCallback(async () => {
    if (!canEditResponsible) return
    
    try {
      const response = await stockAuditService.getEligibleResponsibles()
      if (response.success && response.data) {
        setEligibleUsers(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar usuários elegíveis:', error)
    }
  }, [canEditResponsible])

  const loadAuditData = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      const response = await stockAuditService.getById(parseInt(id))
      if (response.success && response.data) {
        const data = response.data
        // Mapear campos do backend para o frontend
        if (data.code && !data.audit_number) {
          data.audit_number = data.code
        }
        if (data.brand_name_full && !data.brand_name) {
          data.brand_name = data.brand_name_full
        }
        setAuditData(data)
        setSelectedResponsibleId(data.responsible_user_id || null)
        // Mapear campos do backend para o frontend
        const mappedItems = Array.isArray(data.items) ? data.items.map((item: any) => {
          const countedQty = item.audited_quantity ?? item.counted_quantity
          const expectedQty = item.effective_expected_quantity ?? item.expected_quantity
          const diff = item.discrepancy ?? (countedQty !== null && countedQty !== undefined 
            ? countedQty - expectedQty 
            : null)
          
          // Calcular status baseado nos dados do backend
          let status: 'pending' | 'counted' | 'divergent' | 'matched' = 'pending'
          if (countedQty !== null && countedQty !== undefined) {
            if (item.has_discrepancy === true || (diff !== null && diff !== 0)) {
              status = 'divergent'
            } else {
              status = 'matched'
            }
          }
          
          return {
            ...item,
            counted_quantity: countedQty,
            difference: diff ?? undefined,
            status: item.status || status
          }
        }) : []
        setItems(mappedItems)
      } else {
        showToast('Auditoria não encontrada', 'error')
        navigate('/erp/stock/audits')
      }
    } catch (error) {
      console.error('Erro ao carregar auditoria:', error)
      showToast('Erro ao carregar auditoria', 'error')
      navigate('/erp/stock/audits')
    } finally {
      setLoading(false)
    }
  }, [id, navigate])

  useEffect(() => {
    loadAuditData()
    loadEligibleUsers()
  }, [loadAuditData, loadEligibleUsers])

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('[data-user-dropdown]')) {
        setShowUserDropdown(false)
      }
      if (!target.closest('[data-status-dropdown]')) {
        setShowStatusDropdown(false)
      }
    }
    if (showUserDropdown || showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserDropdown, showStatusDropdown])

  const handleUpdateResponsible = async () => {
    if (!id) return

    try {
      setUpdatingResponsible(true)
      const response = await stockAuditService.updateResponsible(parseInt(id), selectedResponsibleId)
      if (response.success) {
        showToast('Responsável atualizado com sucesso!', 'success')
        setShowResponsibleEdit(false)
        loadAuditData()
      } else {
        showToast(response.message || 'Erro ao atualizar responsável', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao atualizar responsável:', error)
      showToast(error.response?.data?.message || 'Erro ao atualizar responsável', 'error')
    } finally {
      setUpdatingResponsible(false)
    }
  }

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const handleApplyAudit = async () => {
    if (!id) return

    setShowApplyModal(false)
    
    try {
      setApplying(true)
      const response = await stockAuditService.apply(parseInt(id))
      if (response.success) {
        showToast('Auditoria aplicada com sucesso! O estoque foi atualizado.', 'success')
        loadAuditData()
      } else {
        showToast(response.message || 'Erro ao aplicar auditoria', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao aplicar auditoria:', error)
      showToast(error.response?.data?.message || 'Erro ao aplicar auditoria', 'error')
    } finally {
      setApplying(false)
    }
  }

  const handleCancelAudit = async () => {
    if (!id) return

    if (!cancelReason.trim()) {
      showToast('Por favor, informe o motivo do cancelamento', 'warning')
      return
    }

    setShowCancelModal(false)
    
    try {
      setCancelling(true)
      const response = await stockAuditService.cancel(parseInt(id), {
        reason: cancelReason.trim()
      })
      if (response.success) {
        const message = response.data?.estorned 
          ? 'Auditoria cancelada e alterações revertidas com sucesso!'
          : 'Auditoria cancelada com sucesso!'
        showToast(message, 'success')
        setTimeout(() => {
          navigate('/erp/stock/audits')
        }, 1500)
      } else {
        showToast(response.message || 'Erro ao cancelar auditoria', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao cancelar auditoria:', error)
      showToast(error.response?.data?.message || 'Erro ao cancelar auditoria', 'error')
    } finally {
      setCancelling(false)
      setCancelReason('')
    }
  }

  const handleReopenAudit = async () => {
    if (!id) return

    setShowReopenModal(false)

    try {
      setReopening(true)
      const response = await stockAuditService.reopen(parseInt(id))
      if (response.success) {
        showToast('Auditoria reaberta. Você pode corrigir as contagens.', 'success')
        setTimeout(() => {
          navigate(`/erp/stock/audits/${id}`)
        }, 1000)
      } else {
        showToast(response.message || 'Erro ao reabrir auditoria', 'error')
      }
    } catch (error: any) {
      console.error('Erro ao reabrir auditoria:', error)
      showToast(error.response?.data?.message || 'Erro ao reabrir auditoria', 'error')
    } finally {
      setReopening(false)
    }
  }

  const getDivergentCount = () => {
    return items.filter(item => item.status === 'divergent').length
  }

  const getTotalDifference = () => {
    return items.reduce((sum, item) => sum + (item.difference || 0), 0)
  }

  const filteredItems = items.filter(item => {
    if (!searchTerm && filterStatus === 'all') return true
    
    const matchesSearch = !searchTerm || (
      item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.product_sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.product_barcode?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus
    
    return matchesSearch && matchesFilter
  })

  if (loading) {
    return (
      <div className="audit-management-loading">
        <Loader2 size={32} className="audit-management-spinner" />
        <p>Carregando auditoria...</p>
      </div>
    )
  }

  if (!auditData) {
    return null
  }

  const canApply = auditData.status === 'completed'
  const canReopenAudit = auditData.status === 'completed' && canReopen
  const canCancel = auditData.status !== 'cancelled'
  const isApplied = auditData.status === 'applied'
  const divergentCount = getDivergentCount()
  const totalDifference = getTotalDifference()

  return (
    <div className="audit-management">
      {/* Header */}
      <div className="audit-management-header">
        <div className="audit-management-header-left">
          <button 
            onClick={() => navigate('/erp/stock/audits')} 
            className="audit-management-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="audit-management-title">
              Gerenciar Auditoria {auditData.audit_number}
            </h1>
            <p className="audit-management-subtitle">
              Marca: {auditData.brand_name} • {items.length} itens
              {auditData.responsible_user_name && (
                <> • Responsável: {auditData.responsible_user_name}</>
              )}
            </p>
          </div>
        </div>

        <div className="audit-management-header-right">
          {canEditResponsible && (
            <div style={{ position: 'relative', marginRight: '1rem' }}>
              {!showResponsibleEdit ? (
                <button
                  onClick={() => setShowResponsibleEdit(true)}
                  className="audit-management-btn-secondary"
                >
                  <User size={16} />
                  {auditData.responsible_user_name || 'Sem responsável'}
                  <Edit2 size={14} />
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                  <div className="audit-management-user-dropdown" data-user-dropdown>
                    <button
                      type="button"
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      className="audit-management-user-dropdown-button"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        {selectedResponsibleId ? (
                          <>
                            {(() => {
                              const selectedUser = eligibleUsers.find(u => u.id === selectedResponsibleId)
                              return selectedUser ? (
                                <>
                                  <Avatar name={selectedUser.name} email={selectedUser.email || ''} imageUrl={selectedUser.avatar_url} size="sm" />
                                  <span style={{ fontSize: '0.875rem' }}>
                                    {selectedUser.name} {selectedUser.group_name ? `(${selectedUser.group_name})` : ''}
                                  </span>
                                </>
                              ) : (
                                <span style={{ fontSize: '0.875rem' }}>Sem responsável</span>
                              )
                            })()}
                          </>
                        ) : (
                          <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Sem responsável</span>
                        )}
                      </div>
                      <ChevronDown size={16} style={{ color: '#6b7280', transform: showUserDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                    </button>
                    {showUserDropdown && (
                      <div className="audit-management-user-dropdown-menu">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedResponsibleId(null)
                            setShowUserDropdown(false)
                          }}
                          className={`audit-management-user-dropdown-option ${selectedResponsibleId === null ? 'active' : ''}`}
                        >
                          <span style={{ color: 'var(--text-tertiary)' }}>Sem responsável</span>
                        </button>
                        {eligibleUsers.length === 0 ? (
                          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>
                            Nenhum usuário elegível encontrado
                          </div>
                        ) : (
                          eligibleUsers.map(user => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setSelectedResponsibleId(user.id)
                                setShowUserDropdown(false)
                              }}
                              className={`audit-management-user-dropdown-option ${selectedResponsibleId === user.id ? 'active' : ''}`}
                            >
                              <Avatar name={user.name} email={user.email || ''} imageUrl={user.avatar_url} size="sm" />
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <span style={{ fontWeight: 500 }}>{user.name}</span>
                                {user.group_name && (
                                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{user.group_name}</span>
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleUpdateResponsible}
                    disabled={updatingResponsible}
                    className="audit-management-btn-primary"
                  >
                    {updatingResponsible ? <Loader2 size={14} className="audit-management-spinner" /> : 'Salvar'}
                  </button>
                  <button
                    onClick={() => {
                      setShowResponsibleEdit(false)
                      setSelectedResponsibleId(auditData.responsible_user_id || null)
                      setShowUserDropdown(false)
                    }}
                    className="audit-management-btn-tertiary"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}
          {canReopenAudit && (
            <button
              onClick={() => setShowReopenModal(true)}
              disabled={reopening}
              className="audit-management-btn-secondary"
            >
              {reopening ? (
                <>
                  <Loader2 size={16} className="audit-management-spinner" />
                  Reabrindo...
                </>
              ) : (
                <>
                  <RotateCcw size={16} />
                  Voltar para Contagem
                </>
              )}
            </button>
          )}
          {canApply && (
            <button 
              onClick={() => setShowApplyModal(true)}
              disabled={applying}
              className="audit-management-btn-primary"
            >
              {applying ? (
                <>
                  <Loader2 size={16} className="audit-management-spinner" />
                  Aplicando...
                </>
              ) : (
                <>
                  <Package size={16} />
                  Aplicar ao Estoque
                </>
              )}
            </button>
          )}
          {canCancel && (
            <button 
              onClick={() => setShowCancelModal(true)}
              disabled={cancelling}
              className="audit-management-btn-danger"
            >
              {cancelling ? (
                <>
                  <Loader2 size={16} className="audit-management-spinner" />
                  Cancelando...
                </>
              ) : (
                <>
                  <X size={16} />
                  Cancelar Auditoria
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="audit-management-summary">
        <div className="audit-management-summary-card">
          <div className="audit-management-summary-icon audit-management-summary-total">
            <Package size={24} />
          </div>
          <div className="audit-management-summary-content">
            <p className="audit-management-summary-label">Total de Itens</p>
            <p className="audit-management-summary-value">{items.length}</p>
          </div>
        </div>
        <div className="audit-management-summary-card">
          <div className="audit-management-summary-icon audit-management-summary-divergent">
            <AlertTriangle size={24} />
          </div>
          <div className="audit-management-summary-content">
            <p className="audit-management-summary-label">Com Divergências</p>
            <p className="audit-management-summary-value">{divergentCount}</p>
          </div>
        </div>
        <div className="audit-management-summary-card">
          <div className="audit-management-summary-icon audit-management-summary-difference">
            {totalDifference >= 0 ? (
              <TrendingUp size={24} />
            ) : (
              <TrendingDown size={24} />
            )}
          </div>
          <div className="audit-management-summary-content">
            <p className="audit-management-summary-label">Diferença Total</p>
            <p className={`audit-management-summary-value ${totalDifference >= 0 ? 'positive' : 'negative'}`}>
              {totalDifference >= 0 ? '+' : ''}{totalDifference}
            </p>
          </div>
        </div>
        <div className="audit-management-summary-card">
          <div className="audit-management-summary-icon audit-management-summary-status">
            <CheckCircle2 size={24} />
          </div>
          <div className="audit-management-summary-content">
            <p className="audit-management-summary-label">Status</p>
            <p className="audit-management-summary-value">
              {auditData.status === 'completed' && 'Concluída'}
              {auditData.status === 'applied' && 'Aplicada'}
              {auditData.status === 'cancelled' && 'Cancelada'}
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="audit-management-filters">
        <div className="audit-management-search">
          <input
            type="text"
            placeholder="Buscar produto por nome, SKU ou código de barras..."
            className="audit-management-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="audit-management-status-filter-wrapper" data-status-dropdown>
          <button
            type="button"
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className="audit-management-status-filter-button"
          >
            <Filter size={14} />
            <span>
              {filterStatus === 'all' && 'Todos os Status'}
              {filterStatus === 'matched' && 'OK'}
              {filterStatus === 'divergent' && 'Divergente'}
              {filterStatus === 'counted' && 'Contado'}
              {filterStatus === 'pending' && 'Pendente'}
            </span>
            <ChevronDown size={14} style={{ transform: showStatusDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </button>
          {showStatusDropdown && (
            <div className="audit-management-status-dropdown-menu">
              <button
                type="button"
                onClick={() => {
                  setFilterStatus('all')
                  setShowStatusDropdown(false)
                }}
                className={`audit-management-status-dropdown-option ${filterStatus === 'all' ? 'active' : ''}`}
              >
                Todos os Status
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterStatus('matched')
                  setShowStatusDropdown(false)
                }}
                className={`audit-management-status-dropdown-option ${filterStatus === 'matched' ? 'active' : ''}`}
              >
                OK
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterStatus('divergent')
                  setShowStatusDropdown(false)
                }}
                className={`audit-management-status-dropdown-option ${filterStatus === 'divergent' ? 'active' : ''}`}
              >
                Divergente
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterStatus('counted')
                  setShowStatusDropdown(false)
                }}
                className={`audit-management-status-dropdown-option ${filterStatus === 'counted' ? 'active' : ''}`}
              >
                Contado
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterStatus('pending')
                  setShowStatusDropdown(false)
                }}
                className={`audit-management-status-dropdown-option ${filterStatus === 'pending' ? 'active' : ''}`}
              >
                Pendente
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="audit-management-card">
        <div className="audit-management-table-wrapper">
          <table className="audit-management-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th className="text-center">Esperado</th>
                <th className="text-center">Contado</th>
                <th className="text-center">Diferença</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="audit-management-empty">
                    <p>Nenhum item encontrado</p>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const hasDifference = item.difference !== undefined && item.difference !== 0
                  
                  return (
                    <tr key={item.id} className="audit-management-table-row">
                      <td>
                        <div className="audit-management-product-info">
                          <span className="audit-management-product-name">
                            {item.product_name || `Produto #${item.product_id}`}
                          </span>
                          {item.product_sku && (
                            <span className="audit-management-product-sku">
                              SKU: {item.product_sku}
                            </span>
                          )}
                          {item.location_code && (
                            <span className="audit-management-product-location">
                              📍 {item.location_code}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="audit-management-quantity-badge">
                          {item.effective_expected_quantity || item.expected_quantity}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="audit-management-quantity-badge">
                          {item.counted_quantity ?? '-'}
                        </span>
                      </td>
                      <td className="text-center">
                        {hasDifference ? (
                          <span className={`audit-management-difference ${item.difference! > 0 ? 'positive' : 'negative'}`}>
                            {item.difference! > 0 ? (
                              <TrendingUp size={12} />
                            ) : (
                              <TrendingDown size={12} />
                            )}
                            {item.difference! > 0 ? '+' : ''}{item.difference}
                          </span>
                        ) : (
                          <span className="audit-management-no-difference">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <span className={`audit-management-item-status audit-management-item-status-${item.status}`}>
                          {item.status === 'matched' && 'OK'}
                          {item.status === 'divergent' && 'Divergente'}
                          {item.status === 'counted' && 'Contado'}
                          {item.status === 'pending' && 'Pendente'}
                        </span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className={`audit-management-toast audit-management-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Reopen Modal */}
      <ConfirmModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        onConfirm={handleReopenAudit}
        type="warning"
        title="Voltar para Contagem"
        message={
          <div>
            <p>Deseja reabrir esta auditoria para correção das contagens?</p>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              A auditoria voltará para <strong>em andamento</strong> e você poderá ajustar as quantidades contadas antes de aplicar ao estoque.
            </p>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              O estoque <strong>não será alterado</strong> — esta ação só é possível antes de aplicar a auditoria.
            </p>
          </div>
        }
        confirmText="Voltar para Contagem"
        cancelText="Cancelar"
      />

      {/* Apply Modal */}
      <ConfirmModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onConfirm={handleApplyAudit}
        type="warning"
        title="Aplicar Auditoria ao Estoque"
        message={
          <div>
            <p>Tem certeza que deseja aplicar as alterações de estoque desta auditoria?</p>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              <strong>Esta ação irá:</strong>
            </p>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
              <li>Atualizar o estoque dos produtos com divergências</li>
              <li>Registrar movimentações no histórico</li>
              <li>Marcar a auditoria como aplicada</li>
            </ul>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#ef4444' }}>
              Esta ação não pode ser desfeita.
            </p>
          </div>
        }
        confirmText="Aplicar ao Estoque"
        cancelText="Cancelar"
      />

      {/* Cancel Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false)
          setCancelReason('')
        }}
        onConfirm={handleCancelAudit}
        type="danger"
        title={isApplied ? "Cancelar e Reverter Auditoria" : "Cancelar Auditoria"}
        message={
          <div>
            <p>Tem certeza que deseja cancelar esta auditoria?</p>
            {isApplied && (
              <div style={{ 
                marginTop: '0.75rem', 
                padding: '0.75rem', 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: '#991b1b'
              }}>
                <p style={{ margin: 0, fontWeight: 600, marginBottom: '0.5rem' }}>
                  ⚠️ Esta auditoria já foi aplicada ao estoque.
                </p>
                <p style={{ margin: 0 }}>
                  Ao cancelar, todas as alterações de estoque serão <strong>revertidas automaticamente</strong> e registradas no histórico.
                </p>
              </div>
            )}
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                Motivo do cancelamento: <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Informe o motivo do cancelamento..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ef4444'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
              {isApplied 
                ? 'A auditoria será marcada como cancelada e todas as alterações de estoque serão revertidas.'
                : 'A auditoria será marcada como cancelada e não poderá ser aplicada ao estoque.'}
            </p>
          </div>
        }
        confirmText={isApplied ? "Cancelar e Reverter" : "Cancelar Auditoria"}
        cancelText="Voltar"
      />
    </div>
  )
}

export default AuditManagementPage
